/* eslint-disable keyword-spacing */
/* eslint-disable react/jsx-key */
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_COLLAPSED } from 'src/components/sidebar/sidebar.config';
import { isLytRole, isTrafficViewerRole } from 'src/constants/roles.constants';
import CircleLogo from 'src/assets/images/vector/lyt_icons/circle-logo.svg';
import { FEATURE_FLAG_KEYS } from 'src/config/featureFlags';
import { isFeatureEnabledSelector } from 'src/store/feature-flags/feature-flags.selectors';

import { sidebarCollapsedSelector } from 'src/components/sidebar/sidebar.selectors';
import { toggleSidebarCollapse } from 'src/components/sidebar/sidebar.actions';
import styled from 'styled-components';

// import LytMenuItem from 'src/components/library/menu-item/LytMenuItem.jsx';
import LytSidebarMenuItem from 'src/components/library/menu-item/LytSidebarMenuItem.jsx';
import LytTooltip from 'src/components/library/tooltip/LytTooltip';
import LytButton from 'src/components/library/button/LytButton';
import LytThemeSelector from 'src/components/sidebar/components/selectors-groupe/LytThemeSelector.jsx';
import LytAgencySelector from 'src/components/sidebar/components/selectors-groupe/LytAgencySelector.jsx';
import LytJurisdictionSelector from 'src/components/sidebar/components/selectors-groupe/LytJurisdictionSelector.jsx';
import Profile from 'src/components/sidebar/components/profile/profile.jsx';
import { userRoleSelector } from 'src/store/user/user.selectors';
import { selectedAgencyNameSelector } from 'src/store/user-info/user-info.selectors';
import { sidebarNavSelector } from 'src/config/navigation.selectors';

export const smallerWidth = 1439;
export const smallerHeight = 880 + 44 * 2;

/**
 * container for the entire sidebar
 */
const SidebarContainer = styled.div`
    display: flex;
    flex-direction: column;
    z-index: 10;
    min-width: ${SIDEBAR_WIDTH_EXPANDED}px;
    max-width: ${SIDEBAR_WIDTH_EXPANDED}px;
    min-height: 100vh;
    height: 100vh;
    background-color: ${({ theme }) => theme?.components?.sidebar?.background};
    border-right: 1px solid ${({ theme }) => theme?.components?.sidebar?.borderRight || 'transparent'};
    position: sticky;
    padding-bottom: 8px;
    top: 0;
    overflow-y: auto;
    max-height: 100vh;
    &.sidebar-collapsed {
        min-width: ${SIDEBAR_WIDTH_COLLAPSED}px;
        max-width: ${SIDEBAR_WIDTH_COLLAPSED}px;
    }
`;

/**
 * Header section containing logo and collapse button
 * Matches Figma design: horizontal when expanded, vertical when collapsed
 */
const HeaderContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'collapsed',
})`
    display: flex;
    flex-direction: ${props => props.collapsed ? 'column' : 'row'};
    align-items: center;
    justify-content: ${props => props.collapsed ? 'center' : 'space-between'};
    padding: 16px 16px;
    gap: 16px;
`;



/**
 * styled section containing the LYT logo
 */
const LogoContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'collapsed',
})`
    display: flex;
    justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
    align-items: center;
    flex: ${props => props.collapsed ? '0' : '1'};
    gap: 6px;
    
    svg {
        width: ${props => props.collapsed ? '24px' : '32px'};
        height: ${props => props.collapsed ? '24px' : '32px'};
    }
`;

const EarlyAccessLabel = styled.span`
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.04em;
    text-transform: lowercase;
    color: ${({ theme }) => theme?.components?.sidebar?.section_title?.color || '#8c8c8c'};
`;

/**
 * container for the routes items in the sidebar
 */
const RoutesContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const FooterSection = styled.div`
    display: flex;
    flex-direction: column;
`;

const SectionTitle = styled.p`
    color: ${({ theme }) => theme.components.sidebar.section_title.color};
    padding: 16px 16px 8px 16px;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 1.33;
    text-transform: uppercase;
`;

const LytSidebar = () => {
    const dispatch = useDispatch();
    const primaryNavElement = useRef(null);

    const collapsed = useSelector(sidebarCollapsedSelector);
    const userRole = useSelector(userRoleSelector);
    const agencyName = useSelector(selectedAgencyNameSelector);
    const sidebarNav = useSelector(sidebarNavSelector);
    const isEarlyAccess = useSelector((state) =>
        isFeatureEnabledSelector(state, FEATURE_FLAG_KEYS.UI_EARLY_ACCESS_INDICATOR));
    
    useEffect(() => {
        if (isEarlyAccess) {
            console.log('EA Mode');
        }
    }, [isEarlyAccess]);
    
    // Check if user is a traffic viewer
    const isTrafficViewer = isTrafficViewerRole(userRole);
    
    // Check if user is an LYT-internal user (lyt_admin, lyt_employee)
    const isLytUser = isLytRole(userRole);

    /**
     * handle minimize button click
     */
    const handleMinimizeButtonClick = () => {
        dispatch(toggleSidebarCollapse(!collapsed));
    };

    /**
     * helper function for rendering a list of links
     * @param {Array} data - a list of link data's given from sidebar.config.js
     * @returns {JSX.Element}
     */
    const renderLinks = useCallback((data) => {
        return data.map(({ title, key, iconName: iconName, href, noIconColor }) =>
            <LytSidebarMenuItem
                id={key}
                key={key}
                title={title}
                href={href}
                iconName={iconName}
                noIconColor={noIconColor}
                collapsed={collapsed}
            >
                {title}
            </LytSidebarMenuItem>
        );
    }, [collapsed]);

    // Registry-driven nav sections (role + agency-type filtered)
    const primaryNavData = sidebarNav?.overview || [];
    const teamNavData = sidebarNav?.teamManagement || [];
    const internalToolsNavData = sidebarNav?.internalTools || [];
    const settingsNavData = sidebarNav?.settings || [];

    return (
        <SidebarContainer className={collapsed ? 'sidebar-collapsed' : ''} >
            <HeaderContainer collapsed={collapsed}>
                <LogoContainer collapsed={collapsed}>
                    <CircleLogo />
                    {isEarlyAccess && (
                        <EarlyAccessLabel data-testid="early-access-indicator">ea</EarlyAccessLabel>
                    )}
                </LogoContainer>
                <LytTooltip content={collapsed ? "Expand sidebar" : "Collapse sidebar"} position="right">
                    <LytButton
                        type="tertiary"
                        icon={collapsed ? "sidebar-expand" : "sidebar-collapse"}
                        onClick={handleMinimizeButtonClick}
                    />
                </LytTooltip>
            </HeaderContainer>
            
            {/* Agency selection section */}
            {(isLytUser || isTrafficViewer) && (
                <>
                    {!collapsed && (
                        <SectionTitle>
                            Agency
                        </SectionTitle>
                    )}
                    
                    {isTrafficViewer && (
                        <LytJurisdictionSelector collapsed={collapsed} />
                    )}
                    
                    {isLytUser && (
                        <LytAgencySelector collapsed={collapsed} />
                    )}
                </>
            )}
            
            {/* Primary navigation section */}
            {!collapsed && (
                <SectionTitle>
                    {(isLytUser || isTrafficViewer)
                        ? 'Operations'
                        : (agencyName || 'Agency')}
                </SectionTitle>
            )}
            
            {/* Agency Navigation Section */}
            <RoutesContainer ref={primaryNavElement}>
                {renderLinks(primaryNavData)}
            </RoutesContainer>
            
            {/* Team Management Section */}
            <FooterSection>
                {!collapsed && <SectionTitle>Team Management</SectionTitle>}
                {renderLinks(teamNavData)}
            </FooterSection>
            
            {/* Internal Tools Section - LYT roles only */}
            {internalToolsNavData.length > 0 && (
                <FooterSection>
                    {!collapsed && <SectionTitle>Internal Tools</SectionTitle>}
                    {renderLinks(internalToolsNavData)}
                </FooterSection>
            )}
            
            {/* Settings Section */}
            <FooterSection>
                {!collapsed && <SectionTitle>Settings</SectionTitle>}
                {renderLinks(settingsNavData)}
                <LytThemeSelector collapsed={collapsed} />
            </FooterSection>
            
            {/* Logout - always last */}
            <Profile collapsed={collapsed} />
        </SidebarContainer>
    );
};

export default LytSidebar;
